/**
 * Lightweight document SEO helpers (no react-helmet dependency).
 */

export const SITE_URL = 'https://livefootball.space';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

export const DEFAULT_SEO = {
  title: 'livefootball.space — Live Football Scores & Match Streams',
  description:
    'Watch live football scores and match streams. Premier League, La Liga, Serie A, Bundesliga, Champions League and more — updated in real time on livefootball.space.',
  path: '/',
  image: DEFAULT_OG_IMAGE,
  robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
  type: 'website'
};

const ROUTE_SEO = [
  {
    test: (p) => p === '/',
    title: 'Live Matches & Scores',
    description:
      'Today’s football fixtures, live scores and kick-offs across Premier League, La Liga, Serie A, Bundesliga and Champions League.'
  },
  {
    test: (p) => p === '/streams' || p.startsWith('/streams/'),
    title: 'Football Streams by League',
    description:
      'Browse live and upcoming football match streams organised by league — top European and world competitions.'
  },
  {
    test: (p) => p === '/scores' || p.startsWith('/scores/'),
    title: 'Football Results & Finished Scores',
    description:
      'Full-time football results and scorelines from major leagues. Search by date, league or team.'
  },
  {
    test: (p) => p === '/news',
    title: 'Football News',
    description:
      'Football news, transfer talk and match analysis from Europe’s biggest leagues and cups.'
  },
  {
    test: (p) => p.startsWith('/news/'),
    title: 'Football News',
    description: 'In-depth football stories and match analysis on livefootball.space.'
  },
  {
    test: (p) => p === '/channels' || p.startsWith('/channels/'),
    title: 'Football Channels',
    description: 'Discover football broadcast channels available on livefootball.space.'
  },
  {
    test: (p) => p.startsWith('/match/'),
    title: 'Match Stream',
    description: 'Watch this football match live with scores and stream options on livefootball.space.'
  },
  {
    test: (p) => p === '/account' || p.startsWith('/account/'),
    title: 'Account',
    description: 'Manage your livefootball.space membership and account settings.',
    robots: 'noindex,nofollow'
  },
  {
    test: (p) => p === '/paywall' || p.startsWith('/paywall/'),
    title: 'Membership',
    description: 'Unlock live football scores and match streams with livefootball.space membership.',
    robots: 'noindex,nofollow'
  },
  {
    test: (p) => p === '/onboarding' || p.startsWith('/onboarding/'),
    title: 'Welcome',
    description: 'Get started with livefootball.space — live scores and football streams.',
    robots: 'noindex,nofollow'
  }
];

function ensureMeta(attr, key, content) {
  if (content == null || content === '') return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function ensureLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function absoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return SITE_URL + '/';
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${SITE_URL}${path}`;
}

export function buildTitle(pageTitle) {
  if (!pageTitle) return DEFAULT_SEO.title;
  if (/livefootball\.space/i.test(pageTitle)) return pageTitle;
  return `${pageTitle} | livefootball.space`;
}

export function seoForPath(pathname) {
  const hit = ROUTE_SEO.find((r) => r.test(pathname || '/'));
  if (!hit) {
    return { ...DEFAULT_SEO, path: pathname || '/' };
  }
  return {
    ...DEFAULT_SEO,
    title: buildTitle(hit.title),
    description: hit.description,
    path: pathname || '/',
    robots: hit.robots || DEFAULT_SEO.robots
  };
}

/**
 * Apply SEO tags to document head.
 * @param {object} opts
 */
export function applySeo(opts = {}) {
  const path = opts.path || window.location.pathname || '/';
  const base = seoForPath(path);
  const title = buildTitle(opts.title || base.title);
  const description = opts.description || base.description;
  const image = absoluteUrl(opts.image || base.image || DEFAULT_OG_IMAGE);
  const url = absoluteUrl(opts.path || base.path || path);
  const robots = opts.robots || base.robots || DEFAULT_SEO.robots;
  const type = opts.type || base.type || 'website';

  document.title = title;
  ensureMeta('name', 'description', description);
  ensureMeta('name', 'robots', robots);
  ensureMeta('name', 'googlebot', robots.includes('noindex') ? 'noindex,nofollow' : 'index,follow');

  ensureLink('canonical', url);

  ensureMeta('property', 'og:site_name', 'livefootball.space');
  ensureMeta('property', 'og:type', type);
  ensureMeta('property', 'og:url', url);
  ensureMeta('property', 'og:title', title);
  ensureMeta('property', 'og:description', description);
  ensureMeta('property', 'og:image', image);
  ensureMeta('property', 'og:image:secure_url', image);
  ensureMeta('property', 'og:image:alt', opts.imageAlt || title);

  ensureMeta('name', 'twitter:card', 'summary_large_image');
  ensureMeta('name', 'twitter:title', title);
  ensureMeta('name', 'twitter:description', description);
  ensureMeta('name', 'twitter:image', image);

  if (opts.jsonLd) {
    let script = document.getElementById('lf-jsonld-page');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'lf-jsonld-page';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(opts.jsonLd);
  }
}

export function setDocumentLang(locale) {
  const lang = locale === 'pt' ? 'pt' : 'en';
  document.documentElement.lang = lang;
  ensureMeta('property', 'og:locale', lang === 'pt' ? 'pt_PT' : 'en_US');
}
